import { Component, OnInit, inject } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, filter, first, switchMap } from 'rxjs/operators';
import { ChartConfiguration, ChartDataset } from 'chart.js';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../../services/auth/auth.service';
import { SalespersonBudget } from '../../../salesperson-budget/models/salesperson-budget';
import { SalespersonBudgetService } from '../../../salesperson-budget/services/salesperson-budget.service';
import {
  BudgetCard,
  DailySalesRow,
  KpiPeriodCard,
  KpiSummaryRow
} from '../../models/salesperson-dashboard.model';
import { SalespersonDashboardService } from '../../services/salesperson-dashboard.service';

const PERIOD_ORDER = ['7d', '30d', 'MTD', 'YTD'];
const PERIOD_LABEL_KEYS: Record<string, string> = {
  '7d': 'LAST_7_DAYS',
  '30d': 'LAST_30_DAYS',
  MTD: 'CURRENT_MONTH',
  YTD: 'YEAR_TO_DATE'
};

@Component({
  selector: 'lib-salesperson-dashboard',
  standalone: false,
  templateUrl: './salesperson-dashboard.component.html',
  styleUrl: './salesperson-dashboard.component.scss'
})
export class SalespersonDashboardComponent implements OnInit {
  private dashboardService = inject(SalespersonDashboardService);
  private budgetService = inject(SalespersonBudgetService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);

  readonly Math = Math;
  loading = true;

  budgetMonthCard: BudgetCard | null = null;
  budgetYtdCard: BudgetCard | null = null;
  budgetYearPct = 0;
  budgetMonthDisplayPct = 0;
  budgetYtdDisplayPct = 0;

  kpiPeriodCards: KpiPeriodCard[] = PERIOD_ORDER.map(p => ({
    period: p,
    labelKey: PERIOD_LABEL_KEYS[p] || p,
    current: null,
    previous: null,
    delta: 0,
    displayAmount: 0
  }));

  // Daily sales chart
  dailySalesType = 'bar' as const;
  dailySalesLabels: string[] = [];
  dailySalesDatasets: ChartDataset<'bar'>[] = [];
  dailySalesOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: true, labels: { color: '#5a6370', font: { size: 11 }, boxWidth: 12, boxHeight: 12 } },
      tooltip: {
        callbacks: {
          label: ctx => {
            const val = Math.round(ctx.parsed.y);
            return ` ${ctx.dataset.label}: ${val.toLocaleString('sv')}`;
          },
          footer: items => {
            const sum = items.reduce((acc, item) => acc + item.parsed.y, 0);
            return `Totalt: ${Math.round(sum).toLocaleString('sv')}`;
          }
        }
      }
    },
    scales: {
      x: { stacked: true, ticks: { color: '#9ca3af', maxRotation: 45, font: { size: 10 } }, grid: { display: false } },
      y: {
        stacked: true,
        ticks: { color: '#9ca3af', font: { size: 10 }, callback: v => Math.round(+v).toLocaleString('sv') },
        grid: { color: 'rgba(6,93,105,0.06)' }
      }
    }
  };

  // Today vs previous business day
  todayRow: DailySalesRow | null = null;
  prevDayRow: DailySalesRow | null = null;
  todayDateLabel = '';
  prevDayDateLabel = '';
  prevDayTitle = 'PREVIOUS_DAY';

  ngOnInit(): void {
    const safe = <T>(obs: Observable<T>) => obs.pipe(catchError(() => of(null as T | null)));

    this.authService
      .getCurrentUser()
      .pipe(
        filter(u => !!u),
        first(),
        switchMap(user => {
          const salespersonCode: string | undefined = user.extra?.salespersonCode;
          const currentYear = new Date().getFullYear();

          return forkJoin({
            kpi: safe(this.dashboardService.getSalesKpiSummary()),
            dailySales: safe(this.dashboardService.getDailySales()),
            budget: salespersonCode
              ? safe(this.budgetService.getByYearAndSalesperson(salespersonCode, currentYear))
              : of<SalespersonBudget | null>(null)
          });
        })
      )
      .subscribe({
        next: ({ kpi, dailySales, budget }) => {
          if (kpi?.length) this.buildKpiPeriods(kpi);
          if (dailySales?.length) {
            this.buildDailySalesChart(dailySales);
            this.buildDailyOverview(dailySales);
          }
          if (budget) this.buildBudgetCards(budget);
          this.loading = false;
          this.startCountUpAnimations();
        },
        error: () => {
          this.loading = false;
        }
      });
  }

  private buildKpiPeriods(data: KpiSummaryRow[]): void {
    const byPeriod = new Map<string, { current: KpiSummaryRow | null; previous: KpiSummaryRow | null }>();

    for (const row of data) {
      const period = (row.period ?? '').toString();
      const segment = (row.segment ?? '').toString().toLowerCase();
      if (!byPeriod.has(period)) byPeriod.set(period, { current: null, previous: null });
      const entry = byPeriod.get(period)!;
      if (segment === 'current') entry.current = row;
      else if (segment === 'previous') entry.previous = row;
    }

    this.kpiPeriodCards = PERIOD_ORDER.map(p => {
      const found = byPeriod.get(p);
      const current = found?.current ?? null;
      const previous = found?.previous ?? null;
      return {
        period: p,
        labelKey: PERIOD_LABEL_KEYS[p] || p,
        current,
        previous,
        delta: this.delta(current?.totalValue ?? 0, previous?.totalValue ?? 0),
        displayAmount: 0
      };
    });
  }

  private startCountUpAnimations(): void {
    this.kpiPeriodCards.forEach((card, i) => {
      const target = card.current?.totalValue ?? 0;
      if (!target) return;
      setTimeout(() => this.animateCountUp(card, target), 200 + i * 80);
    });
  }

  private animateCountUp(card: KpiPeriodCard, target: number): void {
    const duration = 900;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      card.displayAmount = Math.round(target * (1 - Math.pow(1 - progress, 3)));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  private buildDailySalesChart(data: DailySalesRow[]): void {
    const last14 = data.slice(-14);
    const formatDate = (d: string) => d.substring(0, 10).replace(/-/g, '');

    this.dailySalesLabels = last14.map(d => formatDate(d.day));

    const stacked = last14.some(r => r.invoiceValue != null || r.orderStockValue != null);

    if (stacked) {
      const invoiceData = last14.map(d => d.invoiceValue ?? 0);
      const orderStockData = last14.map(d => d.orderStockValue ?? 0);
      const datasets: ChartDataset<'bar'>[] = [
        {
          label: this.translate.instant('TOTAL_INVOICE_AMOUNT'),
          data: invoiceData,
          backgroundColor: '#065D69',
          stack: 'daily'
        }
      ];
      if (orderStockData.some(v => v > 0)) {
        datasets.push({
          label: this.translate.instant('TOTAL_ORDER_STOCK'),
          data: orderStockData,
          backgroundColor: '#DAE7E8',
          stack: 'daily'
        });
      }
      this.dailySalesDatasets = datasets;
    } else {
      this.dailySalesDatasets = [
        {
          label: this.translate.instant('TOTAL_SALES'),
          data: last14.map(d => d.totalValue || 0),
          backgroundColor: '#065D69',
          stack: 'daily'
        }
      ];
    }
  }

  private buildDailyOverview(data: DailySalesRow[]): void {
    const today = new Date();
    const prev = new Date(today);
    prev.setDate(prev.getDate() - 1);

    // If previous day is Sunday, fall back to Friday
    if (prev.getDay() === 0) {
      prev.setDate(prev.getDate() - 2);
      this.prevDayTitle = 'PREVIOUS_FRIDAY';
    } else {
      this.prevDayTitle = 'PREVIOUS_DAY';
    }

    const todayStr = this.toLocalDateStr(today);
    const prevStr = this.toLocalDateStr(prev);

    this.todayDateLabel = today.toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' });
    this.prevDayDateLabel = prev.toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' });

    this.todayRow = data.find(d => d.day.substring(0, 10) === todayStr) ?? null;
    this.prevDayRow = data.find(d => d.day.substring(0, 10) === prevStr) ?? null;
  }

  private toLocalDateStr(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }

  private buildBudgetCards(budget: SalespersonBudget): void {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;

    const mtdActual = this.kpiPeriodCards.find(c => c.period === 'MTD')?.current?.totalValue ?? 0;
    const ytdActual = this.kpiPeriodCards.find(c => c.period === 'YTD')?.current?.totalValue ?? 0;

    const monthBudget = budget.monthlyBudgets[currentMonth] ?? 0;

    let ytdBudget = 0;
    let fullYearBudget = 0;
    for (let m = 1; m <= 12; m++) {
      const v = budget.monthlyBudgets[m] ?? 0;
      fullYearBudget += v;
      if (m <= currentMonth) ytdBudget += v;
    }

    const monthNames = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
    const currentMonthName = monthNames[currentMonth - 1];

    this.budgetMonthCard = {
      periodLabel: currentMonthName,
      actual: mtdActual,
      budget: monthBudget,
      pct: monthBudget ? Math.round((mtdActual / monthBudget) * 100) : 0,
      remaining: monthBudget - mtdActual
    };

    this.budgetYtdCard = {
      periodLabel: `jan–${currentMonthName}`,
      actual: ytdActual,
      budget: ytdBudget,
      pct: ytdBudget ? Math.round((ytdActual / ytdBudget) * 100) : 0,
      remaining: ytdBudget - ytdActual
    };

    this.budgetYearPct = fullYearBudget ? Math.round((ytdActual / fullYearBudget) * 100) : 0;

    setTimeout(() => this.animatePct('month', this.budgetMonthCard!.pct), 300);
    setTimeout(() => this.animatePct('ytd', this.budgetYtdCard!.pct), 400);
  }

  private animatePct(target: 'month' | 'ytd', targetPct: number): void {
    const duration = 1000;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = Math.round(targetPct * eased);
      if (target === 'month') this.budgetMonthDisplayPct = value;
      else this.budgetYtdDisplayPct = value;
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  avgOrder(row: KpiSummaryRow | null): number {
    if (!row || !row.invoiceCount) return 0;
    return Math.round(row.totalValue / row.invoiceCount);
  }

  delta(current: number, previous: number): number {
    if (!previous) return 0;
    return Math.round(((current - previous) / previous) * 100);
  }

  formatValue(val: any): string {
    const n = +val;
    if (!isNaN(n)) {
      return Math.round(n).toLocaleString('sv');
    }
    return val ?? '–';
  }
}
